import React from 'react';
import PropTypes from 'prop-types';
import { Box, List, Grid, Button, ListItem, Typography, ListItemText } from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { buildUrl, getRecurringPayment } from 'utils';
import { useSelector } from 'react-redux';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { useStyles, listTextStyles } from './Subscriptions.styled';

const SubscriptionsList = ({ params, pagination, subscriptions, handleChangePage }) => {
  const classes = useStyles();
  const history = useHistory();
  const listClasses = listTextStyles();
  const { localeFormat } = useSelector((state) => state.organizations);
  const guessTZ = moment.tz.guess();

  /**
   * Parse subscription data
   * @param {object} subscription Object with subscription data
   */
  const getSubscriptionData = (subscription = {}) => {
    const subsInfo = [
      {
        label: i18n.t('subscriptions.vehicle'),
        value: subscription?.vehicle?.name || '--',
      },
      {
        label: i18n.t('subscriptions.periodic_payment'),
        value: getRecurringPayment(subscription?.pricing),
      },
    ];

    return subsInfo;
  };

  const TimezoneComponent = ({ tz = guessTZ }) => (
    <span className={classes.timezone}>{tz ? moment().tz(tz).format('(UTC Z)') : ''}</span>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      className={classes.listBox}
    >
      {subscriptions?.length ? (
        <>
          <List className={classes.listRoot}>
            {subscriptions?.map((subscription, index) => {
              return (
                <Box
                  key={index}
                  className={clsx(classes.container, classes?.[subscription.status])}
                >
                  <Box className={classes.header} width="100%">
                    <Typography color="textPrimary" style={{ lineHeight: 1 }}>
                      <Box
                        fontSize={12}
                        fontWeight="fontWeightMedium"
                        component="span"
                        lineHeight={1}
                      >
                        <Button
                          type="button"
                          variant="text"
                          color="primary"
                          data-cy="view-subscription"
                          disableFocusRipple={true}
                          className={classes.button}
                          onClick={() =>
                            history.push(buildUrl(`/subscriptions/${subscription?.id}`))
                          }
                        >
                          ID: #{subscription?.id}
                        </Button>
                      </Box>
                    </Typography>
                    <Typography
                      style={{ lineHeight: 1 }}
                      className={clsx(classes.status, classes?.[subscription.status])}
                    >
                      <Box
                        fontSize={11}
                        fontWeight="fontWeightMedium"
                        component="span"
                        lineHeight={1}
                      >
                        {i18n.t(`subscriptions.statuses.${subscription.status}`)}
                      </Box>
                    </Typography>
                  </Box>
                  <Box mt={2} width="100%">
                    <Grid container>
                      <Grid item xs={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('subscriptions.start_time')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {subscription?.start_used_time || subscription?.start_reserved_time
                              ? moment(
                                  subscription?.start_used_time || subscription.start_reserved_time
                                )
                                  .tz(guessTZ)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {(subscription?.start_used_time ||
                              subscription?.start_reserved_time) && <TimezoneComponent />}
                          </Box>
                        </Typography>
                      </Grid>
                      <Grid item sm={12} md={6}>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} component="span" fontWeight="fontWeightBold">
                            {i18n.t('subscriptions.end_time')}
                          </Box>
                        </Typography>
                        <Typography className={clsx(classes.textSubtitle, classes.lineHeight)}>
                          <Box mt={2} fontSize={13} lineHeight={1} component="span">
                            {subscription?.end_used_time || subscription?.end_reserved_time
                              ? moment(
                                  subscription?.end_used_time || subscription.end_reserved_time
                                )
                                  .tz(guessTZ)
                                  .format(`${localeFormat} h:mm A`)
                              : '-'}
                            {(subscription?.end_used_time || subscription?.end_reserved_time) && (
                              <TimezoneComponent />
                            )}
                          </Box>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                  <List className={classes.list}>
                    {getSubscriptionData(subscription).map((val, index) => (
                      <ListItem key={index} className={classes.listItem}>
                        <ListItemText
                          classes={listClasses}
                          primary={val.label}
                          secondary={
                            <Box
                              display="flex"
                              component="span"
                              flexDirection="column"
                              className={classes.textRight}
                            >
                              <Typography
                                variant="body2"
                                component="span"
                                color="textSecondary"
                                className={classes?.[val.classes]}
                              >
                                {val.value}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })}
          </List>
          <Paginator
            component="div"
            limit={params.limit}
            pagination={pagination}
            onPageChange={handleChangePage}
          />
        </>
      ) : (
        <Typography color="textPrimary" style={{ lineHeight: 1 }}>
          <Box fontSize={14} component="span" lineHeight={1}>
            {i18n.t('subscriptions.no_subscriptions')}
          </Box>
        </Typography>
      )}
    </Box>
  );
};

SubscriptionsList.propTypes = {
  params: PropTypes.object,
  pagination: PropTypes.object,
  subscriptions: PropTypes.array,
  handleChangePage: PropTypes.func,
};

SubscriptionsList.defaultProps = {
  params: {
    page: 1,
    limit: 3,
  },
  pagination: {},
  subscriptions: [],
};

export default SubscriptionsList;
